import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BookUser, ChevronDown, ChevronRight, RefreshCw, Search, Trash2, FileText, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Livro de Clientes com estatísticas agregadas por cliente:
 *  - Nº de faturas emitidas
 *  - Total faturado (bruto)
 *  - Notas de crédito emitidas
 *  - Líquido (bruto − NC)
 *  - Data da última fatura
 *
 * Clicar num cliente expande e carrega o histórico detalhado de faturas
 * via GET /admin/facturalusa/manual/customers/{id}/history.
 */
export default function CustomerBookSection() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [history, setHistory] = useState({}); // { [customerId]: { invoices, total_invoiced, total_credited, total_net } }
  const [historyLoading, setHistoryLoading] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/facturalusa/manual/customers", {
        params: { with_stats: true, limit: 500 },
      });
      setCustomers(data.items || []);
    } catch {
      toast.error("Não foi possível carregar o livro de clientes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function loadHistory(customerId) {
    setHistoryLoading((h) => ({ ...h, [customerId]: true }));
    try {
      const { data } = await api.get(`/admin/facturalusa/manual/customers/${customerId}/history`);
      setHistory((prev) => ({ ...prev, [customerId]: data }));
    } catch {
      toast.error("Não foi possível carregar o histórico deste cliente.");
    } finally {
      setHistoryLoading((h) => ({ ...h, [customerId]: false }));
    }
  }

  function toggleOpen(customerId) {
    if (openId === customerId) {
      setOpenId(null);
      return;
    }
    setOpenId(customerId);
    if (!history[customerId]) loadHistory(customerId);
  }

  async function deleteCustomer(id, name) {
    if (!window.confirm(`Remover «${name}» do livro de clientes?\n\nAs faturas emitidas permanecem no histórico da Facturalusa. Só o registo de contacto é apagado.`)) return;
    try {
      await api.delete(`/admin/facturalusa/manual/customers/${id}`);
      toast.success("Cliente removido do livro.");
      setCustomers((arr) => arr.filter((c) => c.id !== id));
      if (openId === id) setOpenId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível remover.");
    }
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? customers.filter((c) => (
        (c.name || "").toLowerCase().includes(q)
        || (c.nif || "").toLowerCase().includes(q)
        || (c.email || "").toLowerCase().includes(q)
      ))
    : customers;

  // Aggregate totals across ALL customers currently visible (respects search filter).
  const grandTotals = filtered.reduce((acc, c) => {
    acc.count += c.invoice_count || 0;
    acc.invoiced += c.total_invoiced || 0;
    acc.credited += c.total_credited || 0;
    return acc;
  }, { count: 0, invoiced: 0, credited: 0 });
  grandTotals.net = grandTotals.invoiced - grandTotals.credited;

  return (
    <div className="rounded-2xl border border-amber-300/20 bg-emerald-950/40 overflow-hidden"
         data-testid="customer-book-section">
      <div className="px-4 sm:px-5 py-3 border-b border-amber-300/15 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <BookUser size={16} className="text-amber-300" /> Livro de Clientes ({customers.length})
          </h2>
          <button onClick={load}
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-300/20 bg-emerald-300/5 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-300/10 transition self-start sm:self-auto"
                  data-testid="customer-book-refresh">
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-300/50" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar por nome, NIF ou email..."
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-emerald-950/60 border border-emerald-300/20 text-sm text-emerald-100 placeholder-emerald-300/40 focus:outline-none focus:border-amber-300/50"
              data-testid="customer-book-search"
            />
          </div>

          <div className="text-xs text-emerald-200/70 whitespace-nowrap flex items-center gap-3 flex-wrap"
               data-testid="customer-book-grand-totals">
            <span><span className="text-emerald-200/50">Faturas:</span> <b className="text-emerald-100">{grandTotals.count}</b></span>
            <span><span className="text-emerald-200/50">Bruto:</span> <b className="text-amber-300">{grandTotals.invoiced.toFixed(2)}€</b></span>
            {grandTotals.credited > 0 && (
              <span><span className="text-emerald-200/50">NC:</span> <b className="text-rose-300">−{grandTotals.credited.toFixed(2)}€</b></span>
            )}
            <span><span className="text-emerald-200/50">Líquido:</span> <b className="text-emerald-300">{grandTotals.net.toFixed(2)}€</b></span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-emerald-200/60 text-sm">A carregar…</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center" data-testid="customer-book-empty">
          <BookUser size={32} className="mx-auto text-amber-300/40 mb-2" />
          <p className="text-emerald-200/70 text-sm">
            {q ? "Nenhum cliente corresponde à pesquisa." : "Ainda não há clientes guardados. Use «Guardar cliente» em qualquer fatura para os adicionar aqui."}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-emerald-300/10">
          {filtered.map((c, ci) => {
            const isOpen = openId === c.id;
            const hist = history[c.id];
            const loadingHist = !!historyLoading[c.id];
            return (
              <div key={`${c.id}-${ci}`} data-testid={`customer-book-row-${c.id}`}>
                <button
                  onClick={() => toggleOpen(c.id)}
                  className="w-full text-left p-4 sm:p-5 hover:bg-emerald-300/5 transition flex items-start gap-3"
                  data-testid={`customer-book-toggle-${c.id}`}
                >
                  <span className="mt-1 text-emerald-300/70 shrink-0">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="text-white font-semibold truncate">{c.name}</span>
                      {c.nif && c.nif !== "999999990" && (
                        <span className="text-xs text-emerald-200/60 font-mono">NIF {c.nif}</span>
                      )}
                      {c.email && <span className="text-xs text-emerald-200/60 truncate">{c.email}</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <span className="text-emerald-200/60">
                        Faturas: <b className="text-emerald-100">{c.invoice_count || 0}</b>
                      </span>
                      <span className="text-emerald-200/60">
                        Total faturado: <b className="text-amber-300">{(c.total_invoiced || 0).toFixed(2)}€</b>
                      </span>
                      {(c.total_credited || 0) > 0 && (
                        <span className="text-emerald-200/60">
                          NC: <b className="text-rose-300">−{(c.total_credited || 0).toFixed(2)}€</b>
                        </span>
                      )}
                      <span className="text-emerald-200/60">
                        Líquido: <b className="text-emerald-300">{(c.total_net ?? c.total_invoiced ?? 0).toFixed(2)}€</b>
                      </span>
                      {c.last_issued_at && (
                        <span className="text-emerald-200/50">
                          Última: {new Date(c.last_issued_at).toLocaleDateString("pt-PT")}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); deleteCustomer(c.id, c.name); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault(); e.stopPropagation();
                        deleteCustomer(c.id, c.name);
                      }
                    }}
                    className="shrink-0 rounded-lg border border-red-400/30 bg-red-400/5 px-2.5 py-1.5 text-xs text-red-200 hover:bg-red-400/15 inline-flex items-center gap-1.5 cursor-pointer"
                    data-testid={`customer-book-delete-${c.id}`}
                    title="Remover do livro">
                    <Trash2 size={12} /> Remover
                  </span>
                </button>

                {isOpen && (
                  <div className="px-4 sm:px-5 pb-5 -mt-1"
                       data-testid={`customer-book-history-${c.id}`}>
                    {loadingHist ? (
                      <div className="text-xs text-emerald-200/60 py-3">A carregar histórico…</div>
                    ) : hist ? (
                      hist.invoices.length === 0 ? (
                        <div className="text-xs text-emerald-200/60 py-3">
                          Este cliente ainda não tem faturas registadas neste sistema.
                        </div>
                      ) : (
                        <div className="rounded-xl border border-emerald-300/15 bg-emerald-950/50">
                          <div className="px-4 py-2 border-b border-emerald-300/10 text-xs text-emerald-200/70 flex items-center gap-2 flex-wrap">
                            <FileText size={12} className="text-amber-300" />
                            Histórico de faturas ({hist.invoices.length})
                            <span className="ml-auto flex items-center gap-3 flex-wrap">
                              <span>Bruto: <b className="text-amber-300">{hist.total_invoiced.toFixed(2)}€</b></span>
                              {hist.total_credited > 0 && (
                                <span>NC: <b className="text-rose-300">−{hist.total_credited.toFixed(2)}€</b></span>
                              )}
                              <span>Líquido: <b className="text-emerald-300">{hist.total_net.toFixed(2)}€</b></span>
                            </span>
                          </div>
                          <div className="divide-y divide-emerald-300/10">
                            {hist.invoices.map((inv) => (
                              <div key={inv.invoice_id}
                                   className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs"
                                   data-testid={`customer-book-invoice-${inv.invoice_id}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  {inv.paid ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-400/20 text-emerald-100 uppercase tracking-wider font-bold inline-flex items-center gap-1">
                                      <CheckCircle2 size={10} /> Paga
                                    </span>
                                  ) : (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-200 uppercase tracking-wider font-bold inline-flex items-center gap-1">
                                      <AlertCircle size={10} /> Por receber
                                    </span>
                                  )}
                                  <span className="font-mono text-amber-300 font-semibold">
                                    {inv.invoice_number || `#${inv.invoice_id}`}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0 text-emerald-200/70">
                                  {inv.issued_at && (
                                    <span>Emitida em {new Date(inv.issued_at).toLocaleString("pt-PT")}</span>
                                  )}
                                  {inv.paid && inv.paid_at && (
                                    <span className="text-emerald-300"> · Paga em {new Date(inv.paid_at).toLocaleDateString("pt-PT")}</span>
                                  )}
                                  {inv.credit_total > 0 && (
                                    <span className="text-rose-300"> · Estornado {inv.credit_total.toFixed(2)}€</span>
                                  )}
                                </div>
                                <div className="text-right whitespace-nowrap">
                                  <span className={inv.credit_total > 0 ? "line-through text-emerald-200/40 mr-2" : "font-bold text-emerald-100"}>
                                    {inv.amount.toFixed(2)}€
                                  </span>
                                  {inv.credit_total > 0 && (
                                    <span className="font-bold text-amber-300">{inv.net_amount.toFixed(2)}€</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="text-xs text-red-300/70 py-3">Falha ao carregar histórico.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
